import { useEffect, useState } from "react";
import api from "../api/axios";
import { Link, useNavigate } from "react-router-dom";
import AlertBox from "../components/AlertBox";
import { useTranslation } from "react-i18next"; // <-- 1. استدعاء الترجمة

const API_BASE = "https://store.wageh.net/blog";

export default function CartPage() {
    const nav = useNavigate();
    const { t } = useTranslation(); // <-- 2. تعريف الترجمة

    const [cart, setCart] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [msg, setMsg] = useState("");
    const [busyId, setBusyId] = useState(null);
    const [checkoutBusy, setCheckoutBusy] = useState(false);

    const fetchCart = async () => {
        setLoading(true);
        setErr("");
        try {
            const res = await api.get("/cart");
            const payload = res.data.data ?? res.data;
            setCart(payload.data ?? payload);
        } catch (e) {
            setErr(e.response?.data?.message || t("cart_load_error"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCart();
    }, []);

    const updateQty = async (itemId, qty) => {
        if (qty < 1) return;
        setMsg("");
        setErr("");
        setBusyId(itemId);

        try {
            await api.patch(`/cart/items/${itemId}`, { qty });
            setMsg(t("cart_qty_updated"));
            fetchCart();
        } catch (e) {
            setErr(e.response?.data?.message || t("cart_update_failed"));
        } finally {
            setBusyId(null);
        }
    };

    const removeItem = async (itemId) => {
        setMsg("");
        setErr("");
        setBusyId(itemId);

        try {
            await api.delete(`/cart/items/${itemId}`);
            setMsg(t("cart_item_removed"));
            fetchCart();
        } catch (e) {
            setErr(e.response?.data?.message || t("cart_remove_failed"));
        } finally {
            setBusyId(null);
        }
    };

    const checkout = async () => {
        setErr("");
        setMsg("");
        setCheckoutBusy(true);

        try {
            await api.post("/checkout");
            setMsg(t("checkout_success"));
            nav("/orders");
        } catch (e) {
            setErr(e.response?.data?.message || t("checkout_failed"));
        } finally {
            setCheckoutBusy(false);
        }
    };

    if (loading) {
        return (
            <div className="container py-5 text-center my-5">
                <div className="spinner-border text-primary" style={{ width: "3rem", height: "3rem" }} role="status" />
                <p className="mt-3 text-muted fw-bold">{t("loading_cart")}</p>
            </div>
        );
    }

    const items = cart?.items || [];

    return (
        <div>
            <div className="bg-primary border-top p-4 text-white mb-4 shadow-sm">
                <div className="container">
                    <h1 className="display-6 m-0"><i className="bi bi-cart3 me-3"></i>{t("shopping_cart")}</h1>
                </div>
            </div>

            <div className="container mb-5">
                <AlertBox type="success" message={msg} onClose={() => setMsg("")} />
                <AlertBox type="danger" message={err} onClose={() => setErr("")} />

                {items.length === 0 ? (
                    <div className="alert alert-info text-center py-5 shadow-sm">
                        <i className="bi bi-cart-x display-1 d-block mb-3"></i>
                        <h4 className="alert-heading mb-3">{t("empty_cart")}</h4>
                        <Link to="/products" className="btn btn-primary px-4">
                            {t("browse_products")}
                        </Link>
                    </div>
                ) : (
                    <div className="row g-4">
                        <div className="col-lg-9">
                            <div className="card shadow-sm border-0">
                                <div className="table-responsive">
                                    <table className="table table-borderless align-middle mb-0">
                                        <thead className="text-muted bg-light">
                                            <tr className="small text-uppercase">
                                                <th scope="col" className="ps-4">{t("product_col")}</th>
                                                <th scope="col" width={150} className="text-center">{t("qty_col")}</th>
                                                <th scope="col" width={150}>{t("price_col")}</th>
                                                <th scope="col" className="text-end pe-4" width={100}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {items.map((i) => {
                                                const cleanPath = i.product?.image ? i.product.image.replace(/^\/+/, '').replace(/^storage\//, '').replace(/^uploads\//, '') : "";
                                                const imgSrc = i.product?.image
                                                    ? (i.product.image.startsWith("http") ? i.product.image : `${API_BASE}/uploads/${cleanPath}`)
                                                    : null;

                                                return (
                                                    <tr key={i.id} className="border-bottom">
                                                        <td className="ps-4 py-3">
                                                            <div className="row align-items-center">
                                                                <div className="col-auto d-none d-md-block">
                                                                    {imgSrc ? (
                                                                        <img src={imgSrc} className="rounded border" width="70" height="70" style={{ objectFit: 'contain' }} alt={i.product?.name} />
                                                                    ) : (
                                                                        <div className="bg-light rounded border d-flex align-items-center justify-content-center" style={{ width: '70px', height: '70px' }}>
                                                                            <i className="bi bi-image text-muted"></i>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className="col">
                                                                    <Link to={`/products/${i.product?.id}`} className="text-decoration-none fw-bold text-dark d-block mb-1">
                                                                        {i.product?.name}
                                                                    </Link>
                                                                    <span className="small text-muted d-block">{t("stock_label")}{i.product?.stock ?? "-"}</span>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="input-group input-group-sm mx-auto" style={{ maxWidth: "120px" }}>
                                                                <button
                                                                    className="btn btn-outline-primary"
                                                                    type="button"
                                                                    disabled={busyId === i.id || i.qty <= 1}
                                                                    onClick={() => updateQty(i.id, i.qty - 1)}
                                                                >
                                                                    <i className="bi bi-dash-lg"></i>
                                                                </button>
                                                                <input
                                                                    type="text"
                                                                    className="form-control text-center fw-bold bg-white"
                                                                    value={i.qty}
                                                                    readOnly
                                                                />
                                                                <button
                                                                    className="btn btn-outline-primary"
                                                                    type="button"
                                                                    disabled={busyId === i.id}
                                                                    onClick={() => updateQty(i.id, i.qty + 1)}
                                                                >
                                                                    <i className="bi bi-plus-lg"></i>
                                                                </button>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <var className="price h6 mb-0 text-success fw-bold">${i.line_total}</var>
                                                            <small className="d-block text-muted mt-1">${i.price_snapshot}{t("per_piece")}</small>
                                                        </td>
                                                        <td className="text-end pe-4">
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                disabled={busyId === i.id}
                                                                onClick={() => removeItem(i.id)}
                                                                title={t("remove_from_cart")}
                                                            >
                                                                {busyId === i.id ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-trash"></i>}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="card-footer bg-white py-3">
                                    <Link to="/products" className="btn btn-outline-secondary">
                                        <i className="bi bi-chevron-right me-2"></i> {t("continue_shopping")}
                                    </Link>
                                </div>
                            </div>

                            <div className="alert alert-success mt-4 shadow-sm border-0 d-flex align-items-center">
                                <i className="bi bi-truck fs-4 me-3"></i>
                                <p className="m-0 fw-bold">{t("free_shipping_msg")}</p>
                            </div>
                        </div>

                        <div className="col-lg-3">
                            <div className="card shadow-sm border-0 position-sticky" style={{ top: "20px" }}>
                                <div className="card-header bg-white py-3">
                                    <h5 className="mb-0 fw-bold">{t("order_summary")}</h5>
                                </div>
                                <div className="card-body">
                                    <dl className="row text-muted small mb-2">
                                        <dt className="col-6 fw-normal">{t("subtotal")}</dt>
                                        <dd className="col-6 text-end">${cart.total}</dd>

                                        <dt className="col-6 fw-normal">{t("shipping_fees")}</dt>
                                        <dd className="col-6 text-end text-success">{t("free")}</dd>
                                    </dl>
                                    <hr className="my-3" />
                                    <dl className="row align-items-center mb-4">
                                        <dt className="col-6 h5 mb-0">{t("total")}</dt>
                                        <dd className="col-6 text-end h4 text-success fw-bold mb-0">
                                            ${cart.total}
                                        </dd>
                                    </dl>

                                    <button
                                        className="btn btn-primary w-100 py-2 fw-bold"
                                        disabled={checkoutBusy}
                                        onClick={checkout}
                                    >
                                        {checkoutBusy ? (
                                            <><span className="spinner-border spinner-border-sm me-2"></span> {t("processing")}</>
                                        ) : (
                                            <>{t("checkout_btn")} <i className="bi bi-credit-card ms-2"></i></>
                                        )}
                                    </button>
                                    <small className="text-muted text-center d-block mt-3">
                                        <i className="bi bi-shield-check me-1 text-success"></i> {t("secure_checkout")}
                                    </small>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="bg-light border-top py-5 mt-auto">
                <div className="container">
                    <h6 className="fw-bold mb-3"><i className="bi bi-info-circle me-2"></i>{t("refund_policy")}</h6>
                    <p className="text-muted small lh-lg mb-0">
                        {t("refund_policy_desc")}
                    </p>
                </div>
            </div>
        </div>
    );
}